import {
  PAYMENT_EXTENSION_FEE_TIERS,
  PAYMENT_EXTENSION_MAX_DAYS,
  PAYMENT_EXTENSIONS_PER_TRIP,
  SCHEDULE_DATE_CHANGE_CUTOFF_DAYS,
} from '../constants/paymentPolicy.js';

/**
 * "Can't make this payment?": which days the customer can move a payment to,
 * and what each costs. Mirrors quoteExtension in
 * supabase/functions/_shared/payment-extension.ts, which has the final say
 * (the screen previews every choice with the server before anything changes).
 */

const DAY_MS = 86400000;

function ymd(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  const s = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

export function addCalendarDays(day, n) {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d) + n * DAY_MS).toISOString().slice(0, 10);
}

/** Today on the Pacific calendar the schedule is read on, as YYYY-MM-DD. */
export function pacificTodayYmd(nowMs = Date.now()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(nowMs));
}

export function extensionFeeCentsForDays(days) {
  if (!Number.isInteger(days) || days < 1) return null;
  const tier = PAYMENT_EXTENSION_FEE_TIERS.find((t) => days <= t.maxDays);
  return tier ? tier.feeCents : null;
}

export function readPaymentExtensions(flightData) {
  const raw = flightData?._payment_extensions;
  return Array.isArray(raw) ? raw.filter((e) => e && typeof e === 'object') : [];
}

/** Every Extension Fee on the trip: a cancellation's credit or refund leaves it
 *  out (never refunded; the server does the same, retainedExtensionFeesCents). */
export function retainedExtensionFeesCents(flightData) {
  return readPaymentExtensions(flightData).reduce((sum, e) => sum + Math.max(0, Math.round(Number(e.fee_cents) || 0)), 0);
}

/**
 * @param {object} p
 * @param {object} p.flightData    the booking's flight_data (its _payment_extensions)
 * @param {string} p.dueDate       the payment still owed first (a failed one first), YYYY-MM-DD
 * @param {*}      p.departureDate the trip's departure (payments finish 5 days before it)
 * @param {string} [p.today]       YYYY-MM-DD, Pacific
 * @param {boolean} [p.failed]     the payment already failed: it moves from today
 * @param {string} [p.paymentId]   NMI: the installment row (one move per row)
 * @returns {{ available: false, reason: 'limit'|'already_moved'|'cutoff'|'no_payment' } |
 *           { available: true, fromDate: string, pastDue: boolean, retryDate: string|null, left: number,
 *             tiers: Array<{ maxDays: number, minDays: number, feeCents: number,
 *               days: Array<{ date: string, days: number, allowed: boolean }> }> }}
 */
export function paymentExtensionChoices({ flightData, dueDate, departureDate, today = pacificTodayYmd(), failed = false, paymentId = null }) {
  const due = ymd(dueDate);
  if (!due) return { available: false, reason: 'no_payment' };
  const used = readPaymentExtensions(flightData);
  if (used.length >= PAYMENT_EXTENSIONS_PER_TRIP) return { available: false, reason: 'limit' };
  if (paymentId && used.some((e) => e.payment_id && e.payment_id === paymentId)) return { available: false, reason: 'already_moved' };
  if (used.some((e) => ymd(e.to_date) === due)) return { available: false, reason: 'already_moved' };

  const pastDue = Boolean(failed) || due < today;
  const fromDate = pastDue ? today : due;
  /** A failed payment with a retry already booked for a later day: only days after it. */
  const retryDate = pastDue && due > today ? due : null;
  const departure = ymd(departureDate);
  const lastAllowed = departure ? addCalendarDays(departure, -SCHEDULE_DATE_CHANGE_CUTOFF_DAYS) : null;

  let minDays = 1;
  const tiers = PAYMENT_EXTENSION_FEE_TIERS.map((tier) => {
    const days = [];
    for (let n = minDays; n <= Math.min(tier.maxDays, PAYMENT_EXTENSION_MAX_DAYS); n += 1) {
      const date = addCalendarDays(fromDate, n);
      days.push({ date, days: n, allowed: (!lastAllowed || date <= lastAllowed) && (!retryDate || date > retryDate) });
    }
    const out = { minDays, maxDays: tier.maxDays, feeCents: tier.feeCents, days };
    minDays = tier.maxDays + 1;
    return out;
  });
  if (!tiers.some((t) => t.days.some((d) => d.allowed))) return { available: false, reason: 'cutoff' };
  return { available: true, fromDate, pastDue, retryDate, left: PAYMENT_EXTENSIONS_PER_TRIP - used.length, tiers };
}

const NMI_OPEN_STATUSES = ['pending', 'retrying', 'declined', 'error', 'delinquent'];

/**
 * The payment "Can't make this payment?" would move, picked the way the
 * server picks it, from a trip's schedule (the trip page, the desk and the
 * phone agent all call this, so none of them can offer a card the others
 * don't):
 *   - NMI: the open installment rows, failed ones first, then by date (the
 *     row id goes along: one move per row).
 *   - Stripe: the oldest failed invoice, dated by when it was created; else
 *     the next renewal.
 * Null when there's nothing to move, or when Defera Credit covers the whole
 * next payment (moving a $0 payment would only cost the fee).
 *
 * @param {object} p
 * @param {boolean} p.isNmi
 * @param {Array} [p.rows]          schedule rows: date, amount_cents, is_paid, paid_ahead, fee_skipped,
 *                                  is_auto_correction, credit_applied_cents, _nmi_status, _nmi_attempt_id
 * @param {Array} [p.openInvoices]  Stripe failed invoices: created, amount_remaining_cents
 * @param {string} [p.today]
 */
export function selectPaymentToMove({ isNmi, rows = [], openInvoices = [], today = pacificTodayYmd() }) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  if (isNmi) {
    const isFailed = (r) => r._nmi_status !== 'pending' || String(ymd(r.date)) < today;
    const open = list
      .filter((r) => NMI_OPEN_STATUSES.includes(r._nmi_status) && ymd(r.date))
      .sort((a, b) => (isFailed(a) === isFailed(b) ? String(ymd(a.date)).localeCompare(String(ymd(b.date))) : (isFailed(a) ? -1 : 1)));
    if (!open.length) return null;
    const first = open[0];
    const amount = Math.max(0, Number(first.amount_cents) || 0);
    if (amount <= 0) return null;
    return { dueDate: ymd(first.date), dueAmountCents: amount, failed: isFailed(first), paymentId: first._nmi_attempt_id || null };
  }
  const invoices = Array.isArray(openInvoices) ? openInvoices.filter(Boolean) : [];
  if (invoices.length) {
    const created = invoices.map((inv) => inv.created).filter(Boolean).sort()[0];
    const amount = invoices.reduce((s, inv) => s + Math.max(0, Number(inv.amount_remaining_cents ?? inv.amount_cents) || 0), 0);
    /** Stripe retries a failed invoice itself: a missed payment counts from today, so it is
     *  never dated after today (its UTC created date can be "tomorrow" in Pacific time). */
    const createdDay = created ? ymd(created) : null;
    return { dueDate: createdDay && createdDay <= today ? createdDay : today, dueAmountCents: amount, failed: true, paymentId: null };
  }
  const next = list.find((r) => ymd(r.date) && !r.is_paid && !r.paid_ahead && !r.fee_skipped && !r.is_auto_correction);
  if (!next) return null;
  const net = Math.max(0, (Number(next.amount_cents) || 0) - Math.max(0, Number(next.credit_applied_cents) || 0));
  if (net <= 0) return null;
  return { dueDate: ymd(next.date), dueAmountCents: net, failed: false, paymentId: null };
}
